import { Injectable } from "@angular/core";
import { Language } from "app/shared/models/language.interface";
import { StorageService } from "app/core/services/storage.service";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { DEFAULT_LANGUAGES_VALUES } from "app/shared/constants/language.const";
import { ThemeService } from "app/core/theme/theme.service";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  public languageChange$: Observable<void>;

  private availableLanguagesSource: BehaviorSubject<Language[]> =
    new BehaviorSubject<Language[]>([]);
  public availableLanguages$: Observable<Language[]> =
    this.availableLanguagesSource.asObservable();

  private currentLanguageSource: BehaviorSubject<Language> =
    new BehaviorSubject<Language>(this.mapValueToLanguage("en"));
  public currentLanguage$: Observable<Language> =
    this.currentLanguageSource.asObservable();

  private isLanguageRotationActiveSource: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isLanguageRotationActive$: Observable<boolean> =
    this.isLanguageRotationActiveSource.asObservable();
  private languageRotationInterval: number;

  constructor(
    private themeService: ThemeService,
    private translateService: TranslateService,
  ) {
    this.languageChange$ = this.translateService.onLangChange.pipe(
      tap(() => {
        this.currentLanguageSource.next(
          this.mapValueToLanguage(this.getCurrentLanguage()),
        );
      }),
      map(() => undefined),
    );

    this.themeService.activeTheme$.subscribe(() => {
      // Available languages are different depending on the white-label.
      this.availableLanguagesSource.next(this.getAvailableLanguages());
    });

    this.isLanguageRotationActive$.subscribe((isActive: boolean) => {
      if (isActive) {
        // Using window.setInterval to prevent type error while running unit-tests.
        // See: https://stackoverflow.com/questions/53189729/typescript-error-setinterval-type-timer-is-not-assignable-to-type-number
        this.languageRotationInterval = window.setInterval(
          () => this.applyNextLanguage(),
          1000,
        );
      } else {
        window.clearInterval(this.languageRotationInterval);
      }
    });
  }

  /**
   * @deprecated use availableLanguages$ instead. Make this method private.
   * Creates and returns an array which contains all the available languages by using the language value array
   */
  public getAvailableLanguages(): Language[] {
    const availableLanguages: Language[] = [];
    const languageValues: string[] = this.getAvailableLanguageValues();

    for (const language of languageValues) {
      availableLanguages.push(this.mapValueToLanguage(language));
    }

    return availableLanguages;
  }

  private mapValueToLanguage(value: string): Language {
    return {
      value,
      title: `language.${value}`,
    };
  }

  /**
   * Update the current language
   */
  public updateLanguage(language: string): void {
    StorageService.currentLanguage = language;

    this.setHtmlLang(language);
    this.changeInterfaceLanguage();
  }

  /**
   * Use the translation files of the chosen language
   */
  public changeInterfaceLanguage(): void {
    // TODO Refactor and enable linting.
    this.translateService.use(this.getCurrentLanguage());
  }

  /**
   * Get available language values according to theme
   */
  public getAvailableLanguageValues(): string[] {
    return DEFAULT_LANGUAGES_VALUES;
  }

  public getCurrentLanguage(): string {
    if (!StorageService.currentLanguage) {
      StorageService.currentLanguage = this.getBrowserLanguage();
    }
    return StorageService.currentLanguage || this.getCurrentLanguage();
  }

  public setHtmlLang(lang?: string): void {
    document.documentElement.lang = lang || this.getCurrentLanguage();
  }

  public toggleLanguageRotation(): void {
    this.isLanguageRotationActiveSource.next(
      !this.isLanguageRotationActiveSource.getValue(),
    );
  }

  private applyNextLanguage(): void {
    const availableLanguages: Language[] =
      this.availableLanguagesSource.getValue();
    const currentLanguage: Language = this.currentLanguageSource.getValue();

    const languageIndex: number | undefined = availableLanguages.findIndex(
      (language) => language.value === currentLanguage.value,
    );

    if (languageIndex === -1) return;

    const nextLanguage: Language =
      availableLanguages[languageIndex + 1] || availableLanguages[0];

    this.updateLanguage(nextLanguage.value);
  }

  /*
   * This is called only on the very first app start or when the localStorage got cleaned
   */
  private getBrowserLanguage(): string {
    const browserLanguage: string | undefined =
      this.translateService.getBrowserLang();

    return this.isLanguageSelectable(browserLanguage) && browserLanguage
      ? browserLanguage
      : "en";
  }

  private isLanguageSelectable(language: string | undefined): boolean {
    return language
      ? this.getAvailableLanguageValues().includes(language)
      : false;
  }
}
